/**
 * Client-side image compression.
 * Loads file → resizes to ≤maxDim → re-encodes at progressively lower quality
 * and smaller dimensions until size ≤ maxBytes. Always returns something.
 */
export async function compressImageToDataUrl(file, {
  maxDim = 1200,
  maxBytes = 900_000,  // ≈900KB — keeps MongoDB doc small while preserving detail
  mimeType = "image/jpeg",
  initialQuality = 0.85,
} = {}) {
  if (!file) return null;
  if (!file.type?.startsWith("image/")) throw new Error("Not an image file");

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });

  const render = (dim, quality) => {
    let w = img.width, h = img.height;
    if (w > dim || h > dim) {
      const r = w > h ? dim / w : dim / h;
      w = Math.round(w * r);
      h = Math.round(h * r);
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return canvas.toDataURL(mimeType, quality);
  };

  // Step 1: start at requested dim / quality, scale quality down to 0.4
  let dim = maxDim;
  let q = initialQuality;
  let result = render(dim, q);
  while (result.length > maxBytes && q > 0.4) {
    q = +(q - 0.1).toFixed(2);
    result = render(dim, q);
  }

  // Step 2: if still too big, progressively reduce dimensions
  while (result.length > maxBytes && dim > 320) {
    dim = Math.round(dim * 0.8);
    q = 0.75;
    result = render(dim, q);
    while (result.length > maxBytes && q > 0.4) {
      q = +(q - 0.1).toFixed(2);
      result = render(dim, q);
    }
  }

  return result;
}
